<template>
  <el-select :value="innerValue" multiple filterable
    :placeholder="placeholder" :disabled="disabled || !canLoad" style="width: 100%;"
    :loading="loading" @input="onInput">
    <el-option v-for="t in options" :key="t.Id" :label="teacherOptionLabel(t)" :value="t.Id"></el-option>
  </el-select>
</template>

<script>
import { fetchTeachersByDepartment, fetchTeachingTeachersByDeptId } from '../utils/meetingRoomTeacherCache';
import { normalizeOptionalIdString } from '../utils/meetingRoomForm';
import { normalizeDeptId } from '../utils/deptCascaderProps';

export default {
  name: 'MeetingRoomTeacherMultiSelect',
  props: {
    value: {
      type: Array,
      default: () => []
    },
    /** 学校 ID：未指定 scopeDepartmentId 时按全校拉取 */
    departmentId: {
      type: [Number, String],
      default: 0
    },
    /** 可约父部门 Id */
    scopeDepartmentId: {
      type: [Number, String],
      default: 0
    },
    /** 会议室校区 Id：大于 0 时按 TeacherCampus 过滤 */
    scopeCampusId: {
      type: [Number, String],
      default: 0
    },
    placeholder: {
      type: String,
      default: '请选择教职工'
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      options: [],
      loading: false,
      loadedKey: ''
    };
  },
  computed: {
    useDeptScope() {
      return !!normalizeDeptId(this.scopeDepartmentId);
    },
    canLoad() {
      return this.useDeptScope || !!normalizeDeptId(this.departmentId);
    },
    loadCacheKey() {
      const campus = normalizeDeptId(this.scopeCampusId) || '';
      if (this.useDeptScope) return `dept:v3:${this.scopeDepartmentId}`;
      return `school:${this.departmentId}:campus:${campus}`;
    },
    innerValue() {
      return (this.value || [])
        .map(id => normalizeOptionalIdString(id))
        .filter(Boolean);
    }
  },
  watch: {
    departmentId(val, oldVal) {
      if (!this.useDeptScope && val !== oldVal) {
        this.reload(true);
      }
    },
    scopeDepartmentId(val, oldVal) {
      if (String(val || '') === String(oldVal || '')) return;
      const clearValue = !!normalizeDeptId(oldVal);
      this.reload(clearValue);
    },
    scopeCampusId(val, oldVal) {
      if (String(val || '') === String(oldVal || '')) return;
      this.reload(true);
    },
    value: {
      immediate: true,
      deep: true,
      handler(ids) {
        (ids || []).forEach(id => this.ensureValueInOptions(id));
      }
    }
  },
  mounted() {
    if (this.canLoad) this.loadTeachers();
  },
  methods: {
    normalizeOption(row) {
      const id = normalizeOptionalIdString(row && (row.Id != null ? row.Id : row.id));
      if (!id) return null;
      return {
        Id: id,
        TeacherName: (row && (row.TeacherName || row.teacherName)) || `教职工(${id})`,
        DepartmentName: (row && (row.DepartmentName || row.departmentName)) || ''
      };
    },
    onInput(val) {
      const ids = (val || [])
        .map(id => normalizeOptionalIdString(id))
        .filter(Boolean);
      this.$emit('input', ids);
    },
    teacherOptionLabel(t) {
      if (!t.DepartmentName) return t.TeacherName;
      return `${t.TeacherName}（${t.DepartmentName}）`;
    },
    reload(clearValue) {
      if (clearValue) this.$emit('input', []);
      this.options = [];
      this.loadedKey = '';
      if (this.canLoad) this.loadTeachers();
    },
    ensureValueInOptions(id) {
      const sid = normalizeOptionalIdString(id);
      if (!sid) return;
      if (this.options.some(o => o.Id === sid)) return;
      this.options.push({ Id: sid, TeacherName: `教职工(${sid})` });
    },
    loadTeachers() {
      if (!this.canLoad || this.loading) return;
      if (this.loadedKey === this.loadCacheKey && this.options.length) return;
      this.loading = true;
      const campusId = normalizeDeptId(this.scopeCampusId) || '';
      const loader = this.useDeptScope
        ? fetchTeachingTeachersByDeptId(this.scopeDepartmentId, campusId)
        : fetchTeachersByDepartment(this.departmentId);
      loader.then(rows => {
        this.loading = false;
        this.loadedKey = this.loadCacheKey;
        this.options = (rows || [])
          .map(row => this.normalizeOption(row))
          .filter(Boolean);
        (this.value || []).forEach(id => this.ensureValueInOptions(id));
      }).catch(() => { this.loading = false; });
    }
  }
};
</script>
